import React, { useState, useEffect } from 'react';
import {
    Typography,
    Paper,
    IconButton,
    CircularProgress,
    Chip,
    Grid,
    Card,
    CardContent,
    CardActions
} from '@mui/material';
import { CustomModal, CustomModalTitle, CustomModalContent, CustomModalActions } from './CustomModal';
import { CustomTextField, CustomButton, CustomAlert, CustomBox } from './CustomFormComponents';
import { CustomTabs, CustomTab } from './CustomTabs';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as PreviewIcon,
    Code as CodeIcon,
    Visibility as VisualIcon
} from '@mui/icons-material';
import { Editor } from '@tinymce/tinymce-react';
import axios from 'axios';

function TemplateManagement() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create'); // 'create', 'edit', 'preview'
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [editorMode, setEditorMode] = useState(0); // 0 = Visual, 1 = Code
    const [formData, setFormData] = useState({
        templateName: '',
        subjectLine: '',
        templateHTML: '',
        sortOrder: 999
    });

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/templates');
            setTemplates(response.data.templates);
        } catch (error) {
            setError('Failed to fetch templates');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = async (mode, template = null) => {
        setDialogMode(mode);
        setSelectedTemplate(template);
        setEditorMode(0); // Reset to Visual Editor
        
        if (template && (mode === 'edit' || mode === 'preview')) {
            try {
                const response = await axios.get(`/api/templates/${template.id}`);
                const fullTemplate = response.data.template;
                setFormData({
                    templateName: fullTemplate.name,
                    subjectLine: fullTemplate.subjectLine,
                    templateHTML: fullTemplate.htmlContent,
                    sortOrder: fullTemplate.sortOrder || 999
                });
            } catch (error) {
                setError('Failed to load template details');
                return;
            }
        } else {
            setFormData({
                templateName: '',
                subjectLine: '',
                templateHTML: '',
                sortOrder: 999
            });
        }
        
        setDialogOpen(true);
        setError('');
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedTemplate(null);
        setFormData({ templateName: '', subjectLine: '', templateHTML: '', sortOrder: 999 });
        setError('');
    };

    const handleSubmit = async () => {
        try {
            setError('');
            
            if (dialogMode === 'create') {
                await axios.post('/api/templates', formData);
                setSuccess('Template created successfully');
            } else if (dialogMode === 'edit') {
                await axios.put(`/api/templates/${selectedTemplate.id}`, formData);
                setSuccess('Template updated successfully');
            }
            
            handleCloseDialog();
            fetchTemplates();
        } catch (error) {
            setError(error.response?.data?.error || 'Operation failed');
        }
    };

    const handleDelete = async (template) => {
        if (!window.confirm(`Are you sure you want to delete template "${template.name}"?`)) {
            return;
        }

        try {
            await axios.delete(`/api/templates/${template.id}`);
            setSuccess('Template deleted successfully');
            fetchTemplates();
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to delete template');
        }
    };

    const validateTemplate = async () => {
        try {
            const response = await axios.post('/api/templates/validate', {
                templateHTML: formData.templateHTML
            });
            
            if (response.data.valid) {
                setSuccess(`Template is valid! Found placeholders: ${response.data.placeholders.join(', ')}`);
            } else {
                setError(response.data.message);
            }
        } catch (error) {
            setError('Failed to validate template');
        }
    };

    if (loading) {
        return (
            <CustomBox display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </CustomBox>
        );
    }

    return (
        <CustomBox>
            <CustomBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">Template Management</Typography>
                <CustomButton
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog('create')}
                >
                    Create Template
                </CustomButton>
            </CustomBox>

            {error && <CustomAlert severity="error" sx={{ mb: 2 }}>{error}</CustomAlert>}
            {success && <CustomAlert severity="success" sx={{ mb: 2 }}>{success}</CustomAlert>}

            {templates.length === 0 ? (
                <CustomBox 
                    display="flex" 
                    flexDirection="column" 
                    alignItems="center" 
                    justifyContent="center" 
                    sx={{ mt: 4, mb: 4, textAlign: 'center' }}
                >
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No templates found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Create your first email template to get started
                    </Typography>
                    <CustomButton
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog('create')}
                    >
                        Create First Template
                    </CustomButton>
                </CustomBox>
            ) : (
                <Grid container spacing={2}>
                    {templates.map((template) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={template.id}>
                        <Card 
                            sx={{ 
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'all 0.3s ease',
                                '&:hover': { 
                                    boxShadow: 4,
                                    transform: 'translateY(-2px)'
                                }
                            }}
                        >
                            <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                                <CustomBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                    <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                                        {template.name}
                                    </Typography>
                                    <Chip 
                                        label={`#${template.sortOrder || 999}`} 
                                        size="small" 
                                        color="primary" 
                                        variant="outlined"
                                    />
                                </CustomBox>
                                <Typography 
                                    variant="body2" 
                                    color="text.secondary" 
                                    sx={{ 
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        mb: 1,
                                        minHeight: '2.4em'
                                    }}
                                >
                                    {template.subjectLine}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    By: {template.createdByName || 'Unknown'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Updated: {new Date(template.updatedAt).toLocaleDateString()}
                                </Typography>
                            </CardContent>
                            <CardActions sx={{ pt: 0, justifyContent: 'center' }}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleOpenDialog('preview', template)}
                                    title="Preview"
                                    color="info"
                                >
                                    <PreviewIcon />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => handleOpenDialog('edit', template)}
                                    title="Edit"
                                    color="primary"
                                >
                                    <EditIcon />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => handleDelete(template)}
                                    title="Delete"
                                    color="error"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </CardActions>
                        </Card>
                    </Grid>
                    ))}
                </Grid>
            )}

            {/* Create/Edit/Preview Template Dialog */}
            <CustomModal 
                open={dialogOpen} 
                onClose={handleCloseDialog} 
                maxWidth="lg" 
                fullWidth
                sx={{ '& .MuiDialog-paper': { height: '80vh' } }}
            >
                <CustomModalTitle>
                    {dialogMode === 'create' && 'Create New Template'}
                    {dialogMode === 'edit' && `Edit Template: ${selectedTemplate?.name}`}
                    {dialogMode === 'preview' && `Preview Template: ${selectedTemplate?.name}`}
                </CustomModalTitle>
                <CustomModalContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {error && <CustomAlert severity="error" sx={{ mb: 2 }}>{error}</CustomAlert>}
                    {success && <CustomAlert severity="success" sx={{ mb: 2 }}>{success}</CustomAlert>}
                    
                    {dialogMode !== 'preview' && (
                        <>
                            <CustomBox display="flex" gap={2}>
                                <CustomTextField
                                    fullWidth
                                    label="Template Name"
                                    value={formData.templateName}
                                    onChange={(e) => setFormData({ ...formData, templateName: e.target.value })}
                                    margin="normal"
                                    required
                                />
                                <CustomTextField
                                    label="Sort Order"
                                    type="number"
                                    value={formData.sortOrder}
                                    onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 999 })}
                                    margin="normal"
                                    sx={{ minWidth: '120px' }}
                                    helperText="1 = first"
                                    inputProps={{ min: 1, max: 9999 }}
                                />
                            </CustomBox>
                            <CustomTextField
                                fullWidth
                                label="Subject Line"
                                value={formData.subjectLine}
                                onChange={(e) => setFormData({ ...formData, subjectLine: e.target.value })}
                                margin="normal"
                                required
                                helperText="You can use placeholders like {{customer_full_name}}"
                            />
                        </>
                    )}

                    {dialogMode === 'preview' && (
                        <>
                            <Typography variant="h6" gutterBottom>Subject: {formData.subjectLine}</Typography>
                            <Typography variant="h6" gutterBottom>Content:</Typography>
                        </>
                    )}

                    <CustomBox sx={{ flex: 1, mt: 2 }}>
                        {dialogMode === 'preview' ? (
                            <CustomBox 
                                sx={{ 
                                    border: '1px solid #ccc', 
                                    p: 2, 
                                    height: '100%', 
                                    overflow: 'auto',
                                    backgroundColor: '#f9f9f9'
                                }}
                                dangerouslySetInnerHTML={{ __html: formData.templateHTML }}
                            />
                        ) : (
                            <>
                                <CustomBox sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                                    <CustomTabs value={editorMode} onChange={(e, newValue) => setEditorMode(newValue)}>
                                        <CustomTab icon={<VisualIcon />} label="Visual Editor" />
                                        <CustomTab icon={<CodeIcon />} label="HTML Code" />
                                    </CustomTabs>
                                </CustomBox>
                                
                                {editorMode === 0 ? (
                                    <Editor
                                        apiKey={process.env.REACT_APP_TINYMCE_API_KEY || "cidq6j1ldl24ah599vsr9xb1rfl9ewlslc2cc1f3xoetd6zn"}
                                        value={formData.templateHTML}
                                        onEditorChange={(content) => 
                                            setFormData({ ...formData, templateHTML: content })
                                        }
                                        init={{
                                            height: 400,
                                            menubar: false,
                                            plugins: [
                                                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                                                'anchor', 'searchreplace', 'visualblocks', 'fullscreen',
                                                'insertdatetime', 'media', 'table', 'preview', 'help', 'wordcount'
                                            ],
                                            toolbar: 'undo redo | blocks fontsize | ' +
                                                'bold italic underline | forecolor backcolor | ' +
                                                'alignleft aligncenter alignright alignjustify | ' +
                                                'bullist numlist outdent indent | ' +
                                                'link image table | preview fullscreen | ' +
                                                'removeformat help',
                                            content_style: 'body { font-family:Arial,Helvetica,sans-serif; font-size:14px; line-height:1.6; }'
                                        }}
                                    />
                                ) : (
                                    <CustomTextField
                                        fullWidth
                                        multiline
                                        rows={20}
                                        value={formData.templateHTML}
                                        onChange={(e) => setFormData({ ...formData, templateHTML: e.target.value })}
                                        sx={{
                                            '& .custom-textfield-input': {
                                                fontFamily: 'monospace',
                                                fontSize: '14px'
                                            }
                                        }}
                                        placeholder="Enter your HTML code here..."
                                    />
                                )}
                            </>
                        )}
                    </CustomBox>

                    {dialogMode !== 'preview' && (
                        <CustomBox sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" fontWeight="bold" gutterBottom>
                                Available Placeholders:
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Note: Phone numbers are automatically formatted as (xxx) xxx-xxxx.
                            </Typography>
                            <CustomBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {[
                                    '{{customer_full_name}}',
                                    '{{customer_first_name}}',
                                    '{{customer_email}}',
                                    '{{customer_phone}}',
                                    '{{agent_name}}',
                                    '{{agent_phone}}'
                                ].map((placeholder) => (
                                    <Chip
                                        key={placeholder}
                                        label={placeholder}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => {
                                            setFormData({
                                                ...formData,
                                                templateHTML: formData.templateHTML + ' ' + placeholder
                                            });
                                        }}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                ))}
                            </CustomBox>
                        </CustomBox>
                    )}
                </CustomModalContent>
                <CustomModalActions>
                    <CustomButton onClick={handleCloseDialog}>
                        {dialogMode === 'preview' ? 'Close' : 'Cancel'}
                    </CustomButton>
                    {dialogMode !== 'preview' && (
                        <>
                            <CustomButton onClick={validateTemplate} color="info">
                                Validate
                            </CustomButton>
                            <CustomButton onClick={handleSubmit} variant="contained">
                                {dialogMode === 'create' ? 'Create' : 'Update'}
                            </CustomButton>
                        </>
                    )}
                </CustomModalActions>
            </CustomModal>
        </CustomBox>
    );
}

export default TemplateManagement;